import type { components } from "@/api-schema";
import { useMemo } from "react";

type AppRole = components["schemas"]["AppRole"];
type AppRoleAttribute = components["schemas"]["AppRoleAttribute"];

export function useCategorizeRoles(
  application: components["schemas"]["Application"],
  selected: AppRole[],
  attributesByUuid: Map<string, AppRoleAttribute>,
) {
  return useMemo(() => {
    const regularAppRoles = getRegularAppRoles(application.appRoles || []);
    const applicationAttributes = getApplicationAttributes(attributesByUuid, application.uuid);
    const referencedAttributeUuids = collectReferencedAttributeUuids(application.appRoles || []);
    const attributeItems = resolveAttributeItems(applicationAttributes, referencedAttributeUuids, attributesByUuid);

    const { active, inactive } = partitionAppRolesBySelection(regularAppRoles, selected);

    return {
      assignedRoles: active,
      unassignedRoles: inactive,
      assignedAttributes: attributeItems,
    };
  }, [application.appRoles, application.uuid, selected, attributesByUuid]);
}

function getRegularAppRoles(appRoles: AppRole[]): AppRole[] {
  return appRoles.filter((appRole) => !appRole.attribute);
}

function getApplicationAttributes(
  attributesByUuid: Map<string, AppRoleAttribute>,
  applicationUuid: string,
): AppRoleAttribute[] {
  return Array.from(attributesByUuid.values()).filter(
    (attr) => attr.application && typeof attr.application === "object" && attr.application.uuid === applicationUuid,
  );
}

function collectReferencedAttributeUuids(appRoles: AppRole[]): Set<string> {
  const referencedUuids = new Set<string>();
  for (const appRole of appRoles) {
    if (appRole.attribute && typeof appRole.attribute === "object" && appRole.attribute.uuid) {
      referencedUuids.add(appRole.attribute.uuid);
    }
  }
  return referencedUuids;
}

function resolveAttributeItems(
  applicationAttributes: AppRoleAttribute[],
  referencedUuids: Set<string>,
  attributesByUuid: Map<string, AppRoleAttribute>,
): AppRoleAttribute[] {
  if (applicationAttributes.length > 0) {
    return applicationAttributes;
  }
  return Array.from(referencedUuids)
    .map((uuid) => attributesByUuid.get(uuid))
    .filter((attr): attr is AppRoleAttribute => attr !== undefined);
}

function partitionAppRolesBySelection(
  appRoles: AppRole[],
  selectedAppRoles: AppRole[],
): { active: AppRole[]; inactive: AppRole[] } {
  const selectedUuids = new Set(selectedAppRoles.map((role) => role.uuid));
  const active: AppRole[] = [];
  const inactive: AppRole[] = [];

  for (const appRole of appRoles) {
    if (selectedUuids.has(appRole.uuid)) {
      active.push(appRole);
    } else {
      inactive.push(appRole);
    }
  }

  return { active, inactive };
}
